import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import ServiceTypeModal from '../ServiceTypeModal';

const GET_SERVICE_TYPES = gql`
  query GetServiceTypes {
    serviceTypes {
      id
      title
      timeBase
      color
      defaultTime
      requiresChecklist
      requiresCheckIn
      isActive
      isBuiltIn
      checklistId
      canSubmitChecklist
      allowedRoles
      category
      showFinancialFields
      usePropertyFinancialFields
      departments
      useChecklistStyle
      checklist {
        id
        title
        items {
          id
          title
        }
      }
    }
  }
`;

const CREATE_SERVICE_TYPE = gql`
  mutation CreateServiceType(
    $title: String!, 
    $timeBase: TimeBaseType!, 
    $color: String!, 
    $defaultTime: String, 
    $requiresChecklist: Boolean, 
    $requiresCheckIn: Boolean, 
    $canSubmitChecklist: Boolean,
    $allowedRoles: [String!],
    $checklistId: ID,
    $category: JobCategory,
    $showFinancialFields: Boolean,
    $usePropertyFinancialFields: Boolean,
    $departments: [String!],
    $useChecklistStyle: Boolean
  ) {
    createServiceType(
      title: $title, 
      timeBase: $timeBase, 
      color: $color, 
      defaultTime: $defaultTime, 
      requiresChecklist: $requiresChecklist, 
      requiresCheckIn: $requiresCheckIn, 
      canSubmitChecklist: $canSubmitChecklist,
      allowedRoles: $allowedRoles,
      checklistId: $checklistId,
      category: $category,
      showFinancialFields: $showFinancialFields,
      usePropertyFinancialFields: $usePropertyFinancialFields,
      departments: $departments,
      useChecklistStyle: $useChecklistStyle
    ) {
      id
      title
      timeBase
      color
      defaultTime
      requiresChecklist
      requiresCheckIn
      canSubmitChecklist
      isActive
      isBuiltIn
      checklistId
      category
      allowedRoles
      showFinancialFields
      usePropertyFinancialFields
      departments
      useChecklistStyle
      checklist {
        id
        title
        items {
          id
          title
        }
      }
    }
  }
`;

const UPDATE_SERVICE_TYPE = gql`
  mutation UpdateServiceType(
    $id: ID!, 
    $title: String, 
    $timeBase: TimeBaseType, 
    $color: String, 
    $defaultTime: String, 
    $requiresChecklist: Boolean, 
    $requiresCheckIn: Boolean,
    $checklistId: ID,
    $canSubmitChecklist: Boolean,
    $allowedRoles: [String!],
    $category: JobCategory,
    $showFinancialFields: Boolean,
    $usePropertyFinancialFields: Boolean,
    $departments: [String!],
    $useChecklistStyle: Boolean
  ) {
    updateServiceType(
      id: $id, 
      title: $title, 
      timeBase: $timeBase, 
      color: $color, 
      defaultTime: $defaultTime, 
      requiresChecklist: $requiresChecklist, 
      requiresCheckIn: $requiresCheckIn,
      checklistId: $checklistId,
      canSubmitChecklist: $canSubmitChecklist,
      allowedRoles: $allowedRoles,
      category: $category,
      showFinancialFields: $showFinancialFields,
      usePropertyFinancialFields: $usePropertyFinancialFields,
      departments: $departments,
      useChecklistStyle: $useChecklistStyle
    ) {
      id
      title
      timeBase
      color
      defaultTime
      requiresChecklist
      requiresCheckIn
      isActive
      isBuiltIn
      checklistId
      canSubmitChecklist
      category
      allowedRoles
      showFinancialFields
      usePropertyFinancialFields
      departments
      useChecklistStyle
      checklist {
        id
        title
        items {
          id
          title
        }
      }
    }
  }
`;

const TOGGLE_SERVICE_TYPE_STATUS = gql`
  mutation ToggleServiceTypeStatus($id: ID!) {
    toggleServiceTypeStatus(id: $id) {
      id
      isActive
    }
  }
`;

function ServiceTypesSection() {
  const [isServiceTypeModalOpen, setIsServiceTypeModalOpen] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState(null);
  
  const { data: serviceTypesData, loading: serviceTypesLoading, error: serviceTypesError, refetch } = useQuery(GET_SERVICE_TYPES);
  
  const [createServiceType] = useMutation(CREATE_SERVICE_TYPE, {
    refetchQueries: [{ query: GET_SERVICE_TYPES }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setIsServiceTypeModalOpen(false);
      setEditingServiceType(null);
      toast.success('Service type created successfully');
    }
  });
  
    const [updateServiceType] = useMutation(UPDATE_SERVICE_TYPE, {
      refetchQueries: [{ query: GET_SERVICE_TYPES }],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        setIsServiceTypeModalOpen(false);
        setEditingServiceType(null);
        toast.success('Service type updated successfully');
      },
      onError: (error) => {
        console.error('Update mutation error:', error);
        toast.error(error.message || 'Failed to update service type');
      },
      update: (cache, { data: { updateServiceType: updatedServiceType } }) => {
        // Manually update the cache to ensure all fields including allowedRoles are preserved
        try {
          const data = cache.readQuery({ query: GET_SERVICE_TYPES });
          if (data && data.serviceTypes) {
            cache.writeQuery({
              query: GET_SERVICE_TYPES,
              data: {
                serviceTypes: data.serviceTypes.map(st => 
                  st.id === updatedServiceType.id ? updatedServiceType : st
                )
              }
            });
          }
        } catch (error) {
          console.error('Error updating cache:', error);
        }
      }
    });
  
  const [toggleServiceTypeStatus] = useMutation(TOGGLE_SERVICE_TYPE_STATUS, {
    refetchQueries: ['GetServiceTypes'],
    onCompleted: (data) => {
      toast.success(`Service type ${data.toggleServiceTypeStatus.isActive ? 'activated' : 'deactivated'}`);
    }
  });

  const handleSubmit = async (formData) => {
    try {
      // Ensure allowedRoles is always an array (never null or undefined)
      const allowedRoles = Array.isArray(formData.allowedRoles) ? formData.allowedRoles : [];
      
      if (editingServiceType?.isBuiltIn) {
        
        // For built-in types, only send the exact fields allowed
        const departments = Array.isArray(formData.departments) ? formData.departments : [];
        const response = await updateServiceType({
          variables: {
            id: editingServiceType.id,
            requiresChecklist: Boolean(formData.requiresChecklist),
            checklistId: formData.requiresChecklist ? formData.checklistId : null,
            canSubmitChecklist: Boolean(formData.canSubmitChecklist),
            allowedRoles: allowedRoles,
            showFinancialFields: Boolean(formData.showFinancialFields),
            usePropertyFinancialFields: Boolean(formData.usePropertyFinancialFields),
            departments,
            useChecklistStyle: Boolean(formData.useChecklistStyle),
          },
          optimisticResponse: {
            updateServiceType: {
              __typename: 'ServiceType',
              ...editingServiceType,
              requiresChecklist: Boolean(formData.requiresChecklist),
              checklistId: formData.requiresChecklist ? formData.checklistId : null,
              canSubmitChecklist: Boolean(formData.canSubmitChecklist),
              allowedRoles: formData.allowedRoles,
              showFinancialFields: Boolean(formData.showFinancialFields),
              usePropertyFinancialFields: Boolean(formData.usePropertyFinancialFields),
              departments,
              useChecklistStyle: Boolean(formData.useChecklistStyle),
            }
          }
        });
        
        if (response.errors) {
          throw new Error(response.errors[0].message);
        }
        
        // Refetch the service types to ensure we have the latest data
        const refetchResult = await refetch();
        const updatedServiceType = refetchResult.data?.serviceTypes?.find(st => st.id === editingServiceType.id);
        // Update editingServiceType with fresh data so it's available if modal reopens
        if (updatedServiceType) {
          setEditingServiceType(updatedServiceType);
        }
      } else {
        // For custom types, update all fields
        const mutation = editingServiceType ? updateServiceType : createServiceType;
        
        const variables = {
          id: editingServiceType?.id,
          ...formData,
          category: formData.category,
          defaultTime: formData.defaultStartTime,
          requiresChecklist: Boolean(formData.requiresChecklist),
          requiresCheckIn: Boolean(formData.requiresCheckIn),
          checklistId: formData.requiresChecklist ? (formData.checklistId || null) : null,
          canSubmitChecklist: Boolean(formData.canSubmitChecklist),
          allowedRoles: allowedRoles,
          showFinancialFields: Boolean(formData.showFinancialFields),
          usePropertyFinancialFields: Boolean(formData.usePropertyFinancialFields),
          departments: Array.isArray(formData.departments) ? formData.departments : [],
          useChecklistStyle: Boolean(formData.useChecklistStyle),
        };

        const response = await mutation({
          variables
        });
        
        if (response.errors) {
          throw new Error(response.errors[0].message);
        }
        
        // Refetch the service types to ensure we have the latest data
        const refetchResult = await refetch();
        const updatedServiceType = refetchResult.data?.serviceTypes?.find(st => st.id === editingServiceType?.id || st.id === response.data?.createServiceType?.id || st.id === response.data?.updateServiceType?.id);
        // Update editingServiceType with fresh data so it's available if modal reopens
        if (updatedServiceType && editingServiceType) {
          setEditingServiceType(updatedServiceType);
        }
      }
    } catch (error) {
      console.error('Error in onSubmit:', error);
      toast.error(error.message || 'Failed to save service type');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Service Types</h2>
        <button
          onClick={() => {
            setEditingServiceType(null);
            setIsServiceTypeModalOpen(true);
          }}
          className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
        >
          Add Service Type
        </button>
      </div>

      {serviceTypesLoading ? (
        <div>Loading service types...</div>
      ) : serviceTypesError ? (
        <div className="text-red-600">Error loading service types</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Base
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {serviceTypesData?.serviceTypes.map((serviceType) => (
                <tr key={serviceType.id} className={serviceType.isBuiltIn ? 'bg-gray-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {serviceType.title}
                      {serviceType.isBuiltIn && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Built-in
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">{serviceType.timeBase}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      serviceType.isActive 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {serviceType.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-x-2">
                      <button
                        onClick={() => {
                          setEditingServiceType(serviceType);
                          setIsServiceTypeModalOpen(true);
                        }}
                        className="text-sm font-medium text-gray-500 hover:text-gray-700 bg-gray-100 px-3 py-1 rounded-md transition-colors"
                      >
                        {serviceType.isBuiltIn ? 'Configure' : 'Edit'}
                      </button>
                      {!serviceType.isBuiltIn && (
                        <button
                          onClick={() => {
                            toggleServiceTypeStatus({ variables: { id: serviceType.id } });
                          }}
                          className="text-sm font-medium text-gray-500 hover:text-gray-700"
                        >
                          {serviceType.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isServiceTypeModalOpen && (
        <ServiceTypeModal
          key={editingServiceType?.id || 'new'}
          isOpen={isServiceTypeModalOpen}
          onClose={() => {
            setIsServiceTypeModalOpen(false);
            setEditingServiceType(null);
          }}
          onSubmit={handleSubmit}
          serviceType={editingServiceType}
        />
      )}
    </div>
  );
}

export default ServiceTypesSection; 